import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { OrganizationRole } from "common/constants";
import { AdminRolePill } from "../common/AdminRolePill";
import { ScrollFade } from "../common/ScrollFade";
import { TeamMemberSelectRow } from "../common/TeamMemberSelectRow";
import type {
  ProjectMemberRole,
  ProjectMemberRoleOption,
  SelectableMember,
  TeamSectionProps,
} from "./types";

export const TeamSection = ({ team }: TeamSectionProps) => {
  const { t } = useTranslation();

  const roleOptions: ProjectMemberRoleOption[] = [
    { value: "member", label: t("organization_management.roles.member") },
    { value: "manager", label: t("organization_management.roles.manager") },
  ];

  // Single, stable list of every org member; sorting by name keeps a row in
  // place when it moves between the team and available pools on toggle.
  const members: SelectableMember[] = useMemo(() => {
    const combined: SelectableMember[] = [
      ...team.teamMembers.map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        isSelected: true,
        role: member.projectRole,
        organizationRole: member.organizationRole,
      })),
      ...team.availableMembers.map((member) => ({
        id: member.id,
        name: member.name,
        email: member.email,
        isSelected: false,
        role: member.projectRole,
        organizationRole: member.organizationRole,
      })),
    ];
    return combined.sort((first, second) =>
      first.name.localeCompare(second.name),
    );
  }, [team.teamMembers, team.availableMembers]);

  if (team.isLoading) {
    return (
      <div className="border-t border-gray-100 pt-4 flex items-center gap-2 text-sm text-gray-500">
        <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
        {t("project_edit.loading_team")}
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 pt-4">
      <h4 className="text-sm font-medium text-gray-700">
        {t("project_edit.team_members")}
      </h4>
      <p className="text-xs text-gray-500 mb-2 mt-1">
        {t("project_edit.team_hint")}
      </p>
      {members.length === 0 ? (
        <p className="text-sm text-gray-500">{t("project_edit.no_members")}</p>
      ) : (
        <ScrollFade className="space-y-2 max-h-64 pr-1">
          {members.map((member) => (
            <TeamMemberSelectRow<ProjectMemberRole>
              key={member.id}
              name={member.name}
              email={member.email}
              isSelected={member.isSelected}
              role={member.role}
              roleOptions={roleOptions}
              onToggle={() => {
                if (member.isSelected) {
                  team.removeMember(member.id);
                } else {
                  team.addMember(member.id, member.role);
                }
              }}
              onRoleChange={(role) => team.changeRole(member.id, role)}
              lockedRoleLabel={
                member.organizationRole === OrganizationRole.ADMIN ? (
                  <AdminRolePill
                    label={t("organization_management.roles.admin")}
                  />
                ) : undefined
              }
            />
          ))}
        </ScrollFade>
      )}
    </div>
  );
};
